/**
 * Why a pack accept was refused, read from the route's answer. Import-free so it can be unit-tested
 * without the app's dependency graph (see the jest note in CLAUDE.md).
 *
 * The harness names each refusal with a stable `code` beside its English `error` sentence:
 * `own_pack`, `needs_reshare`, `disabled`, `not_available`, `invalid_params`, `no_metasite`
 * (`orion-harness-poc/src/endpoints/packs.ts`, `AcceptPackFailureCode`). Branch on the code, never on
 * the sentence — it is for humans and logs and may be reworded at any time.
 *
 * The screen used to keep two distinctions (401 sign-in, 410 gone) and fold every other failure into
 * "this link has expired", which told a sender who opened their own invite that the link had died and
 * sent the receiver of a torn pack to the wrong remedy. A code the client does not know still lands on
 * `invalid`, so an older client keeps working against a newer harness and the other way round.
 */
export type PackAcceptFailure = { kind: 'unauthenticated' } | { kind: 'gone' } | { kind: 'ownPack' } | { kind: 'needsReshare' } | { kind: 'invalid' };

export function readPackAcceptFailure(status: number, body: unknown): PackAcceptFailure {
  if (status === 401) return { kind: 'unauthenticated' };
  const code = typeof body === 'object' && body !== null && typeof (body as { code?: unknown }).code === 'string' ? (body as { code: string }).code : undefined;
  if (code === 'own_pack') return { kind: 'ownPack' };
  if (code === 'needs_reshare') return { kind: 'needsReshare' };
  if (status === 410 || code === 'disabled') return { kind: 'gone' };
  return { kind: 'invalid' };
}
